import { Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './auth/AuthProvider';
import { DialogProvider } from './ui/Dialogs';
import Login from './auth/Login';
import Confirm from './auth/Confirm';
import ResetPassword from './auth/ResetPassword';
import Onboarding from './onboarding/Onboarding';
import InviteLanding from './onboarding/InviteLanding';
import AppShell from './layout/AppShell';
import BandejaPage from './tareas/BandejaPage';
import AgendaPage from './tareas/AgendaPage';
import TareaPage from './tareas/TareaPage';
import TareaFormPage from './tareas/TareaFormPage';
import BuscarPage from './tareas/BuscarPage';
import GuardadosPage from './tareas/GuardadosPage';
import PerfilPage from './perfil/PerfilPage';
import InformesIndex from './informes/InformesIndex';
import { informes } from './informes/informes-registry';

interface HtmlInformeProps {
  src: string;
  title: string;
}

function HtmlInforme({ src, title }: HtmlInformeProps) {
  return (
    <iframe
      src={src}
      title={title}
      style={{
        position: 'fixed',
        inset: 0,
        width: '100vw',
        height: '100vh',
        border: 0,
        background: '#0f172a',
        zIndex: 10,
      }}
    />
  );
}

function Splash() {
  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc' }}>
      <span style={{ color: '#64748b' }}>Cargando…</span>
    </div>
  );
}

function AppRoutes() {
  const { session, profile, loading, needsOnboarding, activeTeamId } = useAuth();

  if (loading) {
    return <Splash />;
  }

  if (!session) {
    // /auth/confirm tiene que ejecutarse antes de exigir sesión —
    // verifyOtp es lo que crea la sesión. /join es la landing de invitación.
    return (
      <Routes>
        <Route path="/auth/confirm" element={<Confirm />} />
        <Route path="/auth/nueva-password" element={<ResetPassword />} />
        <Route path="/join/:teamId/:token" element={<InviteLanding />} />
        <Route path="*" element={<Login />} />
      </Routes>
    );
  }

  // Sesión iniciada pero el perfil aún no ha cargado: evitamos parpadeo del
  // onboarding mientras resolvemos si tiene equipo.
  if (!profile) {
    return <Splash />;
  }

  // Tiene sesión y perfil pero sin equipo: onboarding. La landing /join sigue
  // accesible para unirse a un equipo concreto desde un enlace.
  if (needsOnboarding) {
    return (
      <Routes>
        <Route path="/join/:teamId/:token" element={<InviteLanding />} />
        <Route path="*" element={<Onboarding />} />
      </Routes>
    );
  }

  // Las rutas a pantalla completa (join, crear-espacio, recuperación) van FUERA
  // del key, para que un cambio de espacio no las remonte a mitad de flujo.
  // key={activeTeamId} envuelve solo el AppShell: al cambiar de espacio se
  // remonta la app principal → todos los hooks refetch y re-suscriben realtime.
  return (
    <Routes>
      {/* Abrir un enlace de invitación: une como espacio adicional (InviteLanding). */}
      <Route path="/join/:teamId/:token" element={<InviteLanding />} />
      {/* Crear un espacio de trabajo adicional desde el selector. */}
      <Route path="/crear-espacio" element={<Onboarding />} />
      {/* Fijar nueva contraseña tras el enlace de recuperación (sesión de recovery activa). */}
      <Route path="/auth/nueva-password" element={<ResetPassword />} />
      <Route element={<div key={activeTeamId ?? 'none'} style={{ display: 'contents' }}><AppShell /></div>}>
        <Route path="/" element={<Navigate to="/bandeja" replace />} />
        <Route path="/bandeja" element={<BandejaPage />} />
        <Route path="/agenda" element={<AgendaPage />} />
        <Route path="/buscar" element={<BuscarPage />} />
        <Route path="/guardados" element={<GuardadosPage />} />
        <Route path="/perfil" element={<PerfilPage />} />
        <Route path="/nueva" element={<TareaFormPage mode="create" />} />
        <Route path="/tareas/:id/editar" element={<TareaFormPage mode="edit" />} />
        <Route path="/tareas/:id" element={<TareaPage />} />
        <Route path="/informes" element={<InformesIndex />} />
        {informes.map((informe) => {
          const Component = informe.componente;
          return (
            <Route
              key={informe.id}
              path={informe.ruta}
              element={
                informe.htmlSrc
                  ? <HtmlInforme src={informe.htmlSrc} title={informe.nombre} />
                  : Component ? <Component /> : null
              }
            />
          );
        })}
        <Route path="*" element={<Navigate to="/bandeja" replace />} />
      </Route>
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <DialogProvider>
        <AppRoutes />
      </DialogProvider>
    </AuthProvider>
  );
}
